import { useTranslation } from '@govard/i18n-core/react';
import { X } from 'lucide-react';
import type { ReactElement } from 'react';
import { Button, Spin, Typography } from 'antd';

import { WidgetContainer } from '../../../components/dashboard/WidgetContainer.js';
import { ViewRenderer } from '../../views/components/ViewRenderer.js';
import type { AgentWidgetListItem } from '../agent-widgets.js';
import {
  useAgentWidgetData,
  useAgentWidgets,
  useDeleteAgentWidget,
  useUpdateAgentWidgetSpec,
} from '../hooks/useAgentWidgets.js';

const { Text } = Typography;

function AgentWidgetContent({
  widget,
  customizing,
}: {
  widget: AgentWidgetListItem;
  customizing: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { data, isLoading } = useAgentWidgetData(widget.id, widget.spec);
  const remove = useDeleteAgentWidget();
  const updateSpec = useUpdateAgentWidgetSpec();

  const removeBlock = (index: number): void => {
    const blocks = widget.spec.blocks.filter((_, blockIndex) => blockIndex !== index);
    if (blocks.length === 0) {
      remove.mutate(widget.id);
      return;
    }
    updateSpec.mutate({
      id: widget.id,
      spec: { ...widget.spec, blocks },
      removedBlockIndex: index,
    });
  };

  return (
    <div data-testid={`agent-widget-${widget.id}`}>
      {customizing ? (
        <div className="mb-3 flex items-center justify-between gap-3">
          <Text strong>{widget.title}</Text>
          <Button
            size="small"
            type="text"
            danger
            icon={<X size={14} />}
            aria-label={t('crm.dashboard.customize.remove', { label: widget.title })}
            loading={remove.isPending}
            onClick={() => remove.mutate(widget.id)}
          />
        </div>
      ) : null}
      {isLoading ? <Spin /> : null}
      {data ? (
        <ViewRenderer
          getRemoveBlockAriaLabel={(block) =>
            t('crm.dashboard.agent_widgets.remove_block', { label: block.title })
          }
          disableBlockRemoval={remove.isPending || updateSpec.isPending}
          onRemoveBlock={removeBlock}
          removingBlockIndex={
            updateSpec.isPending ? updateSpec.variables?.removedBlockIndex : undefined
          }
          result={data}
          showTitle={false}
        />
      ) : null}
    </div>
  );
}

/**
 * Виджеты дашборда, созданные агентом (`generate_dashboard_widget`). Рендерятся
 * `ViewRenderer` по уже скомпилированному spec — те же данные, что пользователь
 * и так может читать. Каждый блок можно закрыть отдельно; удаление всей группы
 * остаётся доступно только в режиме настройки дашборда.
 */
export function AgentWidgetsSection({
  customizing = false,
}: {
  customizing?: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  const { data: widgets, isLoading } = useAgentWidgets();

  if (isLoading) return null;
  if (widgets === undefined || widgets.length === 0) return null;

  return (
    <div data-testid="dashboard-agent-widgets">
      <WidgetContainer
        title={t('crm.dashboard.agent_widgets.title')}
        testId="dashboard-agent-widgets-card"
        minHeight={0}
      >
        <div className="space-y-6">
          {widgets.map((widget) => (
            <AgentWidgetContent key={widget.id} widget={widget} customizing={customizing} />
          ))}
        </div>
      </WidgetContainer>
    </div>
  );
}
